import {
  CoordinatesPolar,
  PerimeterCoordinates,
  PerimeterCoordinatesResponse,
} from '@/models/perimeter-coordinates'
import { AddAndEditPerimeterCoordinatesForm } from '@/schemas/perimeter-coordinates-schema'

import {
  validateCoordinatesDegrees,
  validateCoordinatesMinutes,
  validateCoordinatesSeconds,
  validateDecimalLatitudeCoordinates,
  validateDecimaLongitudeAndLongitudeDegrees,
} from './validate-coordinates'

export function convertRingToCoordinates(
  ring: PerimeterCoordinates[],
  isPolar: boolean,
): number[][] {
  return ring.map((coord) => {
    if (isPolar) {
      const polarLat = coord.latitude_polar
      const polarLng = coord.longitude_polar

      if (!polarLat || !polarLng) return [0, 0]

      const lat = convertPolarToDecimal(polarLat)
      const lng = convertPolarToDecimal(polarLng)

      if (!lat || !lng) return [0, 0]

      return [parseFloat(lng), parseFloat(lat)]
    }

    const decimalLat = coord.latitude_decimal
    const decimalLng = coord.longitude_decimal

    if (!decimalLat || !decimalLng) return [0, 0]

    return [parseFloat(decimalLng), parseFloat(decimalLat)]
  })
}

export function formatPolarCoordinates(
  coordinates: PerimeterCoordinatesResponse,
) {
  return coordinates
    .flatMap((item) => item.coordenadas.flatMap((ring) => ring))
    .map((coord) => ({
      latitude: {
        degrees: coord.latitude_polar?.graus,
        minutes: coord.latitude_polar?.minutos,
        seconds: coord.latitude_polar?.segundos,
        orientation: coord.latitude_polar?.orientacao,
      },
      longitude: {
        degrees: coord.longitude_polar?.graus,
        minutes: coord.longitude_polar?.minutos,
        seconds: coord.longitude_polar?.segundos,
        orientation: coord.longitude_polar?.orientacao,
      },
    }))
}

export function formatDecimalCoordinates(
  coordinates: PerimeterCoordinatesResponse,
) {
  return coordinates
    .flatMap((item) => item.coordenadas.flatMap((ring) => ring))
    .map((coord) => ({
      latitude: coord.latitude_decimal,
      longitude: coord.longitude_decimal,
    }))
}

export function formatPerimeterCoordinatesToPatternFromBackEnd(
  data: AddAndEditPerimeterCoordinatesForm,
): PerimeterCoordinatesResponse | null {
  const decimalCoordinates = data.coordinates.decimal_coordinates
  const polarCoordinates = data.coordinates.polar_coordinates
  const typeCoordinates = data.coordinates.type

  let coordinates: PerimeterCoordinates[] = []

  if (typeCoordinates === 'Polar' && polarCoordinates) {
    coordinates = polarCoordinates.map((coord) => ({
      latitude_polar: {
        graus: coord.latitude.degrees,
        minutos: coord.latitude.minutes,
        segundos: coord.latitude.seconds,
        orientacao: coord.latitude.orientation,
      },
      longitude_polar: {
        graus: coord.longitude.degrees,
        minutos: coord.longitude.minutes,
        segundos: coord.longitude.seconds,
        orientacao: coord.longitude.orientation,
      },
    }))
  } else if (decimalCoordinates) {
    coordinates = decimalCoordinates.map((coord) => ({
      latitude_decimal: coord.latitude,
      longitude_decimal: coord.longitude,
    }))
  }

  const formatted: PerimeterCoordinatesResponse = [
    {
      tipo_coordenada: typeCoordinates,
      coordenadas: [coordinates],
    },
  ]

  return formatted
}

export function convertPolarToDecimal(coord: CoordinatesPolar): string | null {
  const graus = parseFloat(coord.graus)
  const minutos = parseFloat(coord.minutos)
  const segundos = parseFloat(coord.segundos)

  if (isNaN(graus) || isNaN(minutos) || isNaN(segundos)) {
    return null
  }

  let decimal = graus + minutos / 60 + segundos / 3600

  const orientacao = coord.orientacao.toUpperCase()

  if (orientacao === 'S' || orientacao === 'O') {
    decimal = -decimal
  } else if (orientacao !== 'N' && orientacao !== 'L') {
    return null
  }

  return decimal.toFixed(6)
}

export function areAllInputsFilled({
  coordinates,
}: AddAndEditPerimeterCoordinatesForm) {
  if (!coordinates.decimal_coordinates || !coordinates.polar_coordinates) {
    return true
  }

  const isPolar = coordinates.type === 'Polar'

  if (isPolar) {
    const polarCoordinates = coordinates.polar_coordinates
    return (
      polarCoordinates.length >= 3 &&
      polarCoordinates.every((coord) => {
        const { latitude, longitude } = coord
        return (
          latitude.degrees &&
          latitude.minutes &&
          latitude.orientation &&
          latitude.seconds &&
          longitude.degrees &&
          longitude.minutes &&
          longitude.orientation &&
          longitude.seconds
        )
      })
    )
  } else {
    const decimalCoordinates = coordinates.decimal_coordinates
    return (
      decimalCoordinates.length >= 3 &&
      decimalCoordinates.every((coord) => {
        return coord.latitude && coord.longitude
      })
    )
  }
}

export function validateCoordinates({
  coordinates,
}: AddAndEditPerimeterCoordinatesForm): string[] {
  const errorsDetails: string[] = []
  const errorLatDecimal = 'Latitude deve estar entre -85 e 85.'
  const errorLngAndLatMinutes = 'Minutos deve estar entre 0 e 59.'
  const errorLngAndLatSeconds = 'Segundos deve estar entre 0 e 59.9999999.'
  const errorLngDecimal = 'Longitude deve estar entre -180 e 180.'
  const errorLatPolar = 'Latitude deve estar entre 0 e 85.'
  const errorLngPolar = 'Longitude deve estar entre 0 e 180.'
  const errorCardinalPoints = 'Selecione os pontos cardeais.'
  const errorMissingFields = 'Preencha todos os campos.'

  const isPolar = coordinates.type === 'Polar'

  if (isPolar) {
    coordinates.polar_coordinates?.forEach((coord) => {
      // Valida campos de Latitude
      const { latitude, longitude } = coord
      if (
        latitude.degrees ||
        latitude.minutes ||
        latitude.seconds ||
        latitude.orientation
      ) {
        if (
          !latitude.degrees ||
          !latitude.minutes ||
          !latitude.seconds ||
          !latitude.orientation
        ) {
          errorsDetails.push(errorMissingFields)
          return
        }
        const isLatValid = validateCoordinatesDegrees(latitude.degrees)
        const isLatValidMinutes = validateCoordinatesMinutes(latitude.minutes)
        const isLatValidSeconds = validateCoordinatesSeconds(latitude.seconds)

        if (!isLatValid) {
          errorsDetails.push(errorLatPolar)
          return
        }

        if (!isLatValidMinutes) {
          errorsDetails.push(errorLngAndLatMinutes)
          return
        }
        if (!isLatValidSeconds) {
          errorsDetails.push(errorLngAndLatSeconds)
          return
        }
        if (
          !longitude.degrees ||
          !longitude.minutes ||
          !longitude.seconds ||
          !longitude.orientation
        ) {
          errorsDetails.push(errorMissingFields)
          return
        }
      }

      // Valida campos de Longitude
      if (
        longitude.degrees ||
        longitude.minutes ||
        longitude.seconds ||
        longitude.orientation
      ) {
        if (
          !longitude.degrees ||
          !longitude.minutes ||
          !longitude.seconds ||
          !longitude.orientation
        ) {
          errorsDetails.push(errorMissingFields)
          return
        }

        const isLngValid = validateDecimaLongitudeAndLongitudeDegrees(
          longitude.degrees,
        )
        const isLatValidMinutes = validateCoordinatesMinutes(longitude.minutes)
        const isLatValidSeconds = validateCoordinatesSeconds(longitude.seconds)

        if (!isLngValid) {
          errorsDetails.push(errorLngPolar)
          return
        }

        if (!isLatValidMinutes) {
          errorsDetails.push(errorLngAndLatMinutes)
          return
        }
        if (!isLatValidSeconds) {
          errorsDetails.push(errorLngAndLatSeconds)
          return
        }

        if (
          !latitude.degrees ||
          !latitude.minutes ||
          !latitude.seconds ||
          !latitude.orientation
        ) {
          errorsDetails.push(errorMissingFields)
          return
        }
      }

      // Valida a orientação
      if (latitude.degrees || latitude.minutes || latitude.seconds) {
        if (!latitude.orientation) {
          errorsDetails.push(errorCardinalPoints)
        }
      }
      if (longitude.degrees || longitude.minutes || longitude.seconds) {
        if (!longitude.orientation) {
          errorsDetails.push(errorCardinalPoints)
        }
      }
    })
  } else {
    coordinates.decimal_coordinates?.forEach((coord) => {
      const { latitude, longitude } = coord

      if (!longitude || !latitude) {
        errorsDetails.push(errorMissingFields)
        return
      }

      if (longitude || latitude) {
        if (!longitude || !latitude) {
          errorsDetails.push(errorMissingFields)
          return
        }
      }
      if (latitude) {
        const isLatValid = validateDecimalLatitudeCoordinates(latitude)
        if (!isLatValid) {
          errorsDetails.push(errorLatDecimal)
          return
        }
      }

      if (longitude) {
        const isLngValid = validateDecimaLongitudeAndLongitudeDegrees(longitude)
        if (!isLngValid) {
          errorsDetails.push(errorLngDecimal)
        }
      }
    })
  }

  return errorsDetails
}
